import React, { useState, useEffect } from "react";
import { Popover, Box, TextField, Button, Typography } from "@material-ui/core";
import Select from "react-select";
import { useRegions } from "../../hooks/useRegions";
import { useJobProfilesByRegion } from "../../hooks/useJobProfilesByRegion";
import { useSkillDimensionsByJobProfile } from "../../hooks/useSkillDimensionsByJobProfile";
import { useCreateKnowledgeItem } from "../../hooks/useCreateKnowledgeItem";
import { useUpdateKnowledgeItem } from "../../hooks/useUpdateKnowledgeItem";
import Swal from "sweetalert2";

const typeOptions = [
  { value: "Capacità", label: "Capacità" },
  { value: "Conoscenza", label: "Conoscenza" },
];

const AbilityKnowledgeModal = ({
  open,
  onClose,
  isUpdate,
  item,
  defaultRegion,
  defaultJobProfile,
  defaultDimension,
}) => {
  const [name, setName] = useState("");
  const [type, setType] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedJobProfile, setSelectedJobProfile] = useState(null);
  const [selectedDimension, setSelectedDimension] = useState(null);

  const { data: regionsData } = useRegions();
  const { data: jobProfilesData } = useJobProfilesByRegion(selectedRegion?.value);
  const { data: dimensionsData } = useSkillDimensionsByJobProfile(selectedJobProfile?.value);
  const createMutation = useCreateKnowledgeItem();
  const updateMutation = useUpdateKnowledgeItem();

  const regions = regionsData?.regions || [];
  const jobProfiles = jobProfilesData?.jobProfiles || [];
  const dimensions = dimensionsData?.skillDimensions || [];

  const regionOptions = regions.map((r) => ({
    value: r.key || r._id,
    label: r.name,
  }));

  const jobProfileOptions = jobProfiles.map((jp) => ({
    value: jp.key || jp._id,
    label: jp.name,
  }));

  const dimensionSelectOptions = dimensions.map((d) => ({
    value: d.key || d._id,
    label: d.name,
  }));

  useEffect(() => {
    if (!isUpdate) setSelectedJobProfile(null);
  }, [selectedRegion]);

  useEffect(() => {
    if (!isUpdate) setSelectedDimension(null);
  }, [selectedJobProfile]);

  useEffect(() => {
    if (open && isUpdate && item) {
      setName(item.name || "");
      const typeMatch = typeOptions.find((t) => t.value === item.type);
      setType(typeMatch || null);
      setSelectedRegion(defaultRegion);
      setSelectedJobProfile(defaultJobProfile);
      setSelectedDimension(defaultDimension);
    } else if (open && !isUpdate) {
      setName("");
      setType(null);
      setSelectedRegion(defaultRegion || null);
      setSelectedJobProfile(defaultJobProfile || null);
      setSelectedDimension(defaultDimension || null);
    }
  }, [open, isUpdate, item]);

  const isFormValid =
    name.trim() !== "" &&
    type !== null &&
    selectedRegion !== null &&
    selectedJobProfile !== null &&
    selectedDimension !== null;

  const handleSave = () => {
    const payload = {
      name: name.trim(),
      type: type.value,
      parentDimensionId: selectedDimension.value,
    };

    if (isUpdate) {
      updateMutation.mutate(
        { id: item.key || item._id, input: payload },
        {
          onSuccess: () => {
            onClose();
            Swal.fire({
              icon: "success",
              title: "Ability / Knowledge updated!",
              showConfirmButton: false,
              timer: 1500,
            });
          },
          onError: (error) => {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: error?.response?.data?.message || error.message,
              showConfirmButton: true,
            });
          },
        }
      );
    } else {
      createMutation.mutate(
        { input: payload },
        {
          onSuccess: () => {
            onClose();
            Swal.fire({
              icon: "success",
              title: "Ability / Knowledge created!",
              showConfirmButton: false,
              timer: 1500,
            });
          },
          onError: (error) => {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: error?.response?.data?.message || error.message,
              showConfirmButton: true,
            });
          },
        }
      );
    }
  };

  return (
    <Popover
      open={open}
      onClose={onClose}
      anchorReference="none"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
      }}
      PaperProps={{
        style: {
          padding: "24px",
          maxWidth: "800px",
          width: "100%",
          margin: "auto",
          overflow: "visible",
        },
      }}
    >
      <Box>
        <Typography variant="h6" gutterBottom>
          {isUpdate ? "Update Ability / Knowledge" : "New Ability / Knowledge"}
        </Typography>

        <TextField
          required
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          margin="normal"
        />

        <Box style={{ marginTop: "8px", marginBottom: "8px" }}>
          <Typography variant="body2" style={{ marginBottom: "4px" }}>
            Type *
          </Typography>
          <Select
            value={type}
            onChange={(value) => setType(value)}
            options={typeOptions}
            isClearable
            placeholder="Select Type"
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </Box>

        <Box style={{ marginTop: "8px", marginBottom: "8px" }}>
          <Typography variant="body2" style={{ marginBottom: "4px" }}>
            Region *
          </Typography>
          <Select
            value={selectedRegion}
            onChange={(value) => setSelectedRegion(value)}
            options={regionOptions}
            isClearable
            placeholder="Select Region"
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </Box>

        <Box style={{ marginTop: "8px", marginBottom: "8px" }}>
          <Typography variant="body2" style={{ marginBottom: "4px" }}>
            Professional Qualification *
          </Typography>
          <Select
            value={selectedJobProfile}
            onChange={(value) => setSelectedJobProfile(value)}
            options={jobProfileOptions}
            isClearable
            isDisabled={!selectedRegion}
            placeholder="Select Professional Qualification"
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </Box>

        <Box style={{ marginTop: "8px", marginBottom: "16px" }}>
          <Typography variant="body2" style={{ marginBottom: "4px" }}>
            Unit of Competence *
          </Typography>
          <Select
            value={selectedDimension}
            onChange={(value) => setSelectedDimension(value)}
            options={dimensionSelectOptions}
            isClearable
            isDisabled={!selectedJobProfile}
            placeholder="Select Unit of Competence"
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </Box>

        <Box style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={
              !isFormValid ||
              createMutation.isLoading ||
              updateMutation.isLoading
            }
          >
            {isUpdate ? "Update" : "Save"}
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};

export default AbilityKnowledgeModal;
